import { Link } from "react-router-dom";
import { Check, ChevronDown, HelpCircle, Minus, Wrench } from "lucide-react";
import { type HelpArticle, type Role, type RoleCell, meetsRole, roleLabels } from "./types";
import { getArticle } from "./content";
import { RichText, formatInline } from "./RichText";

const ROLE_ORDER: Role[] = ["viewer", "operator", "tenant_admin", "super_admin"];

function RoleCellIcon({ value }: { value: RoleCell }) {
  if (value === "yes") return <Check size={15} className="mx-auto text-green-600 dark:text-green-400" aria-label="Yes" />;
  if (value === "partial")
    return <span className="mx-auto block text-center text-[11px] font-medium text-riot-gold-600 dark:text-riot-gold-400" aria-label="Partial">Some</span>;
  return <Minus size={15} className="mx-auto text-riot-navy-300 dark:text-riot-navy-600" aria-label="No" />;
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="mb-2 mt-6 text-xs font-semibold uppercase tracking-wider text-riot-navy-400">{children}</h3>
  );
}

interface Props {
  article: HelpArticle;
  currentRole: Role;
  /** Called when a related-article link is clicked (e.g. to close a slide-over). */
  onNavigate?: () => void;
}

export function HelpArticleView({ article, currentRole, onNavigate }: Props) {
  return (
    <div className="text-riot-navy-700 dark:text-riot-navy-200">
      {/* Overview */}
      <RichText text={article.overview} />

      {/* Concepts */}
      {article.concepts && article.concepts.length > 0 && (
        <section>
          <SectionHeading>Key concepts</SectionHeading>
          <dl className="space-y-2.5">
            {article.concepts.map((c) => (
              <div key={c.term} className="rounded-lg border border-riot-navy-100 bg-white p-3 dark:border-riot-navy-700 dark:bg-riot-navy-900">
                <dt className="text-sm font-semibold text-riot-navy-900 dark:text-white">{c.term}</dt>
                <dd className="mt-0.5 text-sm leading-relaxed text-riot-navy-600 dark:text-riot-navy-300">{formatInline(c.definition)}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      {/* Tasks / walkthroughs */}
      {article.tasks && article.tasks.length > 0 && (
        <section>
          <SectionHeading>How do I…</SectionHeading>
          <div className="space-y-2">
            {article.tasks.map((task) => {
              const allowed = meetsRole(currentRole, task.minRole);
              return (
                <details key={task.id} className="group rounded-lg border border-riot-navy-100 bg-white dark:border-riot-navy-700 dark:bg-riot-navy-900">
                  <summary className="flex cursor-pointer items-center gap-2 px-3.5 py-2.5 text-sm font-medium text-riot-navy-800 marker:content-none dark:text-riot-navy-100">
                    <ChevronDown size={15} className="shrink-0 text-riot-navy-400 transition-transform group-open:rotate-180" />
                    <span className="flex-1">{task.title}</span>
                    {task.minRole && (
                      <span
                        className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                          allowed
                            ? "bg-riot-navy-100 text-riot-navy-500 dark:bg-riot-navy-700 dark:text-riot-navy-300"
                            : "bg-riot-gold-100 text-riot-gold-700 dark:bg-riot-gold-500/15 dark:text-riot-gold-300"
                        }`}
                      >
                        {allowed ? roleLabels[task.minRole] : `Needs ${roleLabels[task.minRole]}`}
                      </span>
                    )}
                  </summary>
                  <div className="border-t border-riot-navy-100 px-3.5 py-3 dark:border-riot-navy-700">
                    {task.intro && <p className="mb-2 text-sm text-riot-navy-600 dark:text-riot-navy-300">{formatInline(task.intro)}</p>}
                    {!allowed && task.minRole && (
                      <p className="mb-2 rounded-md bg-riot-gold-50 px-2.5 py-1.5 text-xs text-riot-gold-800 dark:bg-riot-gold-500/10 dark:text-riot-gold-300">
                        Your role can't perform this. It needs <strong>{roleLabels[task.minRole]}</strong> or higher — ask a Company admin.
                      </p>
                    )}
                    <ol className="ml-4 list-decimal space-y-2 text-sm text-riot-navy-700 dark:text-riot-navy-200">
                      {task.steps.map((step, i) => (
                        <li key={i} className="pl-1 leading-relaxed">
                          {formatInline(step.text)}
                          {step.note && (
                            <p className="mt-1 text-xs italic text-riot-navy-500 dark:text-riot-navy-400">{formatInline(step.note)}</p>
                          )}
                        </li>
                      ))}
                    </ol>
                    {task.result && (
                      <p className="mt-3 flex items-start gap-1.5 rounded-md bg-green-50 px-2.5 py-1.5 text-xs text-green-800 dark:bg-green-500/10 dark:text-green-300">
                        <Check size={14} className="mt-0.5 shrink-0" />
                        <span>{formatInline(task.result)}</span>
                      </p>
                    )}
                  </div>
                </details>
              );
            })}
          </div>
        </section>
      )}

      {/* FAQs */}
      {article.faqs && article.faqs.length > 0 && (
        <section>
          <SectionHeading>Frequently asked</SectionHeading>
          <div className="space-y-2">
            {article.faqs.map((faq, i) => (
              <details key={i} className="group rounded-lg border border-riot-navy-100 bg-white dark:border-riot-navy-700 dark:bg-riot-navy-900">
                <summary className="flex cursor-pointer items-center gap-2 px-3.5 py-2.5 text-sm font-medium text-riot-navy-800 marker:content-none dark:text-riot-navy-100">
                  <HelpCircle size={15} className="shrink-0 text-riot-navy-400" />
                  <span className="flex-1">{faq.q}</span>
                  <ChevronDown size={15} className="shrink-0 text-riot-navy-400 transition-transform group-open:rotate-180" />
                </summary>
                <div className="border-t border-riot-navy-100 px-3.5 py-3 text-sm leading-relaxed text-riot-navy-600 dark:border-riot-navy-700 dark:text-riot-navy-300">
                  {formatInline(faq.a)}
                </div>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* Troubleshooting */}
      {article.troubleshooting && article.troubleshooting.length > 0 && (
        <section>
          <SectionHeading>Troubleshooting</SectionHeading>
          <div className="space-y-2.5">
            {article.troubleshooting.map((t, i) => (
              <div key={i} className="rounded-lg border border-riot-navy-100 bg-white p-3 dark:border-riot-navy-700 dark:bg-riot-navy-900">
                <p className="flex items-center gap-1.5 text-sm font-semibold text-riot-navy-900 dark:text-white">
                  <Wrench size={14} className="shrink-0 text-riot-gold-500" />
                  {t.problem}
                </p>
                {t.cause && <p className="mt-1 text-xs text-riot-navy-500 dark:text-riot-navy-400"><strong>Why:</strong> {formatInline(t.cause)}</p>}
                <p className="mt-1 text-sm text-riot-navy-600 dark:text-riot-navy-300"><strong>Fix:</strong> {formatInline(t.fix)}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Role matrix */}
      {article.roleMatrix && article.roleMatrix.length > 0 && (
        <section>
          <SectionHeading>Who can do what</SectionHeading>
          <div className="overflow-x-auto rounded-lg border border-riot-navy-100 dark:border-riot-navy-700">
            <table className="w-full min-w-[28rem] text-left text-sm">
              <thead>
                <tr className="border-b border-riot-navy-100 bg-riot-navy-50 dark:border-riot-navy-700 dark:bg-riot-navy-800">
                  <th className="px-3 py-2 font-medium text-riot-navy-600 dark:text-riot-navy-300">Capability</th>
                  {ROLE_ORDER.map((r) => (
                    <th key={r} className="px-2 py-2 text-center text-xs font-medium text-riot-navy-600 dark:text-riot-navy-300">{roleLabels[r]}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {article.roleMatrix.map((row, i) => (
                  <tr key={i} className="border-b border-riot-navy-100 last:border-0 dark:border-riot-navy-700">
                    <td className="px-3 py-2 text-riot-navy-700 dark:text-riot-navy-200">{row.capability}</td>
                    {ROLE_ORDER.map((r) => (
                      <td key={r} className="px-2 py-2"><RoleCellIcon value={row[r]} /></td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Related */}
      {article.related && article.related.length > 0 && (
        <section>
          <SectionHeading>Related guides</SectionHeading>
          <div className="flex flex-wrap gap-2">
            {article.related.map((id) => {
              const rel = getArticle(id);
              // Never surface a guide the current role can't open — it would be a dead link.
              if (!rel || !meetsRole(currentRole, rel.minRole)) return null;
              const Icon = rel.icon;
              return (
                <Link
                  key={id}
                  to={`/help/${rel.id}`}
                  onClick={onNavigate}
                  className="inline-flex items-center gap-1.5 rounded-full border border-riot-navy-200 bg-white px-3 py-1.5 text-xs font-medium text-riot-navy-700 hover:border-riot-gold-300 hover:bg-riot-gold-50 dark:border-riot-navy-600 dark:bg-riot-navy-800 dark:text-riot-navy-200 dark:hover:border-riot-gold-500/40 dark:hover:bg-riot-navy-700"
                >
                  <Icon size={13} className="text-riot-gold-500" />
                  {rel.title}
                </Link>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
